import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { F0Dialog } from '@factorialco/f0-react'
import { Box, Button, NoticeBox, Spinner } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'

import { observer } from 'mobx-react'

import { EmployeesEmployeeSelectorInput } from 'generated/resources/graphql'

import DayPicker from 'modules/core/components/Inputs/DayPicker'
import Text from 'modules/core/components/Inputs/Text'
import Modal from 'modules/core/components/Modal'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import ResourceTypes from 'modules/processes/components/ResourceTypes'
import { ProcessInputFields } from 'modules/processes/domain'
import { useBulkCreateMaterializedProcess } from 'modules/processes/hooks/useBulkCreateMaterializedProcess'
import { InputField } from 'modules/processes/hooks/useCreateMaterializedProcess'
import useGetProcessConfigurationStatus from 'modules/processes/hooks/useProcessSettiingsValidation'
import { useTemplate } from 'modules/processes/hooks/useTemplate'
import { useProcessContext } from 'modules/processes/pages/Process/ProcessRootPage'

const ASYNC_PROCESSES_LIMIT = 1

type Props = {
  processId: number
  onClose: () => void
}

interface DataTypeMap {
  [key: string]: string
}

function createHash(arr: ProcessInputFields[] | undefined) {
  if (!arr) return {}
  return arr.reduce((hash: DataTypeMap, item): object => {
    hash[item.key] = item.data_type
    return hash
  }, {})
}

const createForm = (processInputFields: ProcessInputFields[] | undefined) => {
  if (typeof processInputFields !== 'undefined') {
    return new MaterializeProcessForm(createHash(processInputFields))
  } else {
    return new MaterializeProcessForm({})
  }
}

class MaterializeProcessForm extends Form {
  constructor(dynamic_schema: object) {
    super({}, dynamic_schema)
  }
}

const MaterializeProcessModal = observer(({ processId, onClose }: Props) => {
  const { category } = useParams()

  const { company } = useCurrentSession()
  const { template, loading: loadingTemplate } = useTemplate(category)
  const [selection, setSelection] = useState<EmployeesEmployeeSelectorInput>({
    type: 'manual',
    ids: [],
  })
  const { bulkCreateMaterializedProcesses } = useBulkCreateMaterializedProcess()
  const processInputFields = template?.processInputFields
  const customProcessInputFields = (processInputFields || []).filter((field) => field.custom)

  const form = createForm(customProcessInputFields)

  const options = {
    successMessage: i18n.t('processes.materialize_process.async_save.success'),
    successFlashTime: 10000,
  }

  const hasEmployeesSelected =
    selection.type === 'all' || (selection.type === 'manual' && (selection.ids?.length ?? 0) > 0)
  const selectedCount = selection.type === 'manual' ? (selection.ids?.length ?? 0) : 0
  const hasMultipleEmployeesSelected =
    selection.type === 'all' || selectedCount > ASYNC_PROCESSES_LIMIT

  const { performAction, running: materializingProcess } = useAsyncAction(
    async () => {
      // eslint-disable-next-line unused-imports/no-unused-vars
      const { resourceId, ...inputFields } = form.values

      const formFields = form.fields
      const items: InputField[] = Object.entries(inputFields).map(([key, value]) => {
        const dataType = formFields[key]
        return {
          key,
          value,
          dataType: dataType?.type || 'string',
        }
      })

      const rawResult = await bulkCreateMaterializedProcesses({
        companyId: company.id,
        processId,
        selection,
        processInputFields: items,
      })

      // We have to use that if we are not using useForm. Because the useAsync will not handle this
      // and we need to throw the error again explicitly
      // TODO: Remove this moving to useHook
      const result = handleResult(rawResult, (_errors) => {})

      if (!result.success) {
        throw new Error()
      }

      track(trackingEvents.processes.processIntanceAdded, {
        category,
      })

      onClose()
    },
    hasMultipleEmployeesSelected ? options : {}
  )

  const completedMandatoryField = customProcessInputFields.every((field) => {
    return field.mandatory === false || form.fieldsWithValueKeys.includes(field.key)
  })

  const disabled =
    materializingProcess || (!form?.isDirty && !hasEmployeesSelected) || !completedMandatoryField // add mandatory fields from the processInput

  const buttonText =
    selection.type === 'all' || selectedCount > 1
      ? i18n.t('processes.materialize_process.form.button.many')
      : i18n.t('processes.materialize_process.form.button.one')

  return (
    <F0Dialog
      isOpen
      width="sm"
      title={i18n.t('processes.materialize_process.title')}
      onSubmit={performAction}
      onClose={() => {
        if (!materializingProcess) {
          onClose()
        }
      }}
      primaryAction={{
        disabled: disabled,
        label: buttonText,
        onClick: performAction,
      }}
      secondaryAction={{
        disabled: materializingProcess,
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <Box gap="s16">
        {loadingTemplate && <Spinner />}
        <ResourceTypes selection={selection} setSelection={setSelection} />

        {typeof processInputFields !== 'undefined' &&
          customProcessInputFields.map((field, i) => {
            if (field.data_type == 'date')
              return (
                <DayPicker
                  key={i}
                  mandatory={field.mandatory}
                  readonly={materializingProcess}
                  label={field.label}
                  field={form.get(field.key)}
                />
              )

            return (
              <Text
                key={i}
                mandatory={field.mandatory}
                readonly={materializingProcess}
                label={field.label}
                field={form.get(field.key)}
              />
            )
          })}
        {hasEmployeesSelected && template?.id === 'offboarding' && (
          <NoticeBox
            message={i18n.tp('processes.materialize_process.form.offboarding_warning', {
              count: selectedCount,
            })}
          />
        )}
      </Box>
      {!processInputFields && null}
    </F0Dialog>
  )
})

type PendingConfigurationModalProps = {
  onClose: () => void
}

const PendingConfigurationModal = observer(({ onClose }: PendingConfigurationModalProps) => {
  return (
    <Modal
      illustration="add-block"
      title={i18n.t('processes.materialize_process.pending_configuration.title')}
      description={i18n.t('processes.materialize_process.pending_configuration.description')}
      onClose={onClose}
      footer={
        <Button type="secondary" onClick={onClose}>
          {i18n.t('common.close')}
        </Button>
      }
    />
  )
})

const MaterializeProcessModalPage = () => {
  const { process } = useProcessContext()
  const { id } = useParams()
  const navigate = useNavigate()

  const processId = Number(id)
  const { processValidationData } = useGetProcessConfigurationStatus(process)

  const onClose = () => {
    navigate(-1)
  }

  if (!id) {
    onClose()
    return null
  }

  const status = processValidationData?.validSettings
    ? null
    : processValidationData?.validationMessage

  if (status) {
    return <PendingConfigurationModal onClose={onClose} />
  }

  return <MaterializeProcessModal processId={processId} onClose={onClose} />
}

export default MaterializeProcessModalPage
